import { minBy, sum, uniqWith } from 'es-toolkit';

import { circularPairwise, notNullish, sortBy } from '@paragon/utilities';

import Direction3D from './Direction3D';
import IsometricEmbedding from './IsometricEmbedding';
import Line3D from './Line3D';
import LineSegment2D from './LineSegment2D';
import LineSegment3D from './LineSegment3D';
import Plane3D from './Plane3D';
import Point3D from './Point3D';
import Polygon2D from './Polygon2D';
import Polyhedron from './Polyhedron';
import { angleLessThan, angleLessThanOrEqual, distanceEquals, isDotProductZero } from './units';
import Vector3D from './Vector3D';

/**
 * For performance reasons, we allow certain values to be passed into the constructor instead of computed therein. It is
 * undefined behavior to use these from a source other than `IsometricEmbedding.polygonTo3D`.
 */
interface Polygon3DOptions {
  /** The `IsometricEmbedding` from which this `Polygon3D` was created. */
  readonly embedding: IsometricEmbedding;
  /** The `Polygon2D` embedded in `embedding` from which this `Polygon3D` was created. */
  readonly source: Polygon2D;
}

class Polygon3D {
  readonly vertices: readonly Point3D[];
  readonly edges: readonly LineSegment3D[];

  private _normalDirection: Direction3D | null = null;

  embedding: IsometricEmbedding;
  mappedTo2D: Polygon2D;

  get area(): number {
    return this.mappedTo2D.area;
  }

  get centroid(): Point3D {
    return this.embedding.pointTo3D(this.mappedTo2D.centroid);
  }

  get normalDirection(): Direction3D {
    if (this._normalDirection === null) {
      this._normalDirection = getNormalDirection(this.vertices);
    }
    return this._normalDirection;
  }

  private calculateEmbeddingAndEmbeddedPolygon() {
    const embedding = getEmbedding(this.vertices, this.normalDirection);
    const mappedTo2D = embedding.polygonTo2DWithoutOrientationValidation(this);
    return [embedding, mappedTo2D] as const;
  }

  static fromUnorientedOrUnorderedSegments(segments: readonly LineSegment3D[]): Polygon3D {
    if (segments.length < 3) throw new Error('Not enough segments');
    let unusedSegments = [...segments];
    const usedSegments = [unusedSegments.pop()!];
    while (!usedSegments[usedSegments.length - 1].endPoint.equals(usedSegments[0].basePoint)) {
      const currentEndPoint = usedSegments[usedSegments.length - 1].endPoint;
      const nextSegments = unusedSegments.filter((segment) =>
        segment.endPoints.some((point) => point.equals(currentEndPoint)),
      );
      if (nextSegments.length !== 1) throw new Error('More or fewer than 2 segments meet at a point');
      const nextSegment = nextSegments[0];
      usedSegments.push(nextSegment.basePoint.equals(currentEndPoint) ? nextSegment : nextSegment.reverse());
      unusedSegments = unusedSegments.filter((segment) => segment !== nextSegment);
    }
    if (unusedSegments.length) throw new Error('Some segments are disjoint');
    return new Polygon3D(usedSegments.map(({ basePoint }) => basePoint));
  }

  constructor(vertices: Point3D[], options?: Polygon3DOptions) {
    if (vertices.length < 3) throw new Error('A Polygon should have at least three points');

    this.vertices = vertices;

    const edges: LineSegment3D[] = [];
    for (let i = 0; i < this.vertices.length; i += 1) {
      edges.push(new LineSegment3D(this.vertices[i], this.vertices[(i + 1) % this.vertices.length]));
    }

    this.edges = edges;

    if (options) {
      [this.embedding, this.mappedTo2D] = [options.embedding, options.source];
      this._normalDirection = options.embedding.xAxis.crossProductDirection(options.embedding.yAxis);
    } else {
      [this.embedding, this.mappedTo2D] = this.calculateEmbeddingAndEmbeddedPolygon();
    }

    // TODO Add validation to make sure that the passed points actually form a polygon.
  }

  equals(other: Polygon3D) {
    if (this.edges.length !== other.edges.length) {
      return false;
    }
    const { vertices } = this;
    const offset = other.vertices.findIndex((vertex) => vertex.equals(vertices[0]));
    if (offset < 0) {
      return false;
    }
    const tester = (vertex: Point3D, index: number) => vertices[index + 1].equals(vertex);
    const rotatedOther = [...other.vertices.slice(offset + 1), ...other.vertices.slice(0, offset)];
    const reversedOtherRotated = rotatedOther.toReversed();
    return rotatedOther.every(tester) || reversedOtherRotated.every(tester);
  }

  isVertical(): boolean {
    return isDotProductZero(this.normalDirection.dotProduct(Direction3D.out));
  }

  reverse(): Polygon3D {
    return new Polygon3D(this.vertices.toReversed());
  }

  reverseIf(condition: boolean) {
    return condition ? this.reverse() : this;
  }

  /**
   * Returns the polygon projected onto a Plane
   * If the the polygon is perpendicular to the plane we return null
   */
  projectOntoPlane(plane: Plane3D): Polygon3D | null {
    //if the polygon is perpendicular to the plane, the projection is degenerate, we get just a linesegment;
    return this.normalDirection.isPerpendicularTo(plane.halfSpace.direction)
      ? null
      : new Polygon3D(this.vertices.map((point) => point.projectOntoPlane(plane)));
  }

  asPlane(): Plane3D {
    return Plane3D.fromPerpendicularDirectionAndPoint(this.normalDirection, this.centroid);
  }

  /**
   * Returns the angle formed between the two polygons on their inner side (opposite their normal directions) where they
   * meet. Returns null if the polygons do not meet or meet in an indeterminate way.
   */
  internalAngle(other: Polygon3D): number | null {
    // For the polygons to have an internal angle, they must meet at at least one segment. Furthermore, all segments of
    // meeting on one polygon must be oriented the same, and all the segments of meeting on the other polygon must be
    // oriented oppositely. Start by getting the paired directions of coincident segments.
    const directionPairs = uniqWith(
      this.edges.flatMap((edge) =>
        other.edges.filter(edge.equals.bind(edge)).map((edge2) => [edge.direction, edge2.direction] as const),
      ),
      (pair1, pair2) => pair1[0].equals(pair2[0]) && pair1[1].equals(pair2[1]),
    );

    // Degenerate case—polygons do not touch or touch in a way that does not clearly define a shared inside and outside
    if (directionPairs.length !== 1) return null;

    const [thisSegmentDirection, otherSegmentDirection] = directionPairs[0];

    // Degenerate case—polygons touch in a way that does not clearly define a shared inside and outside
    if (thisSegmentDirection.equals(otherSegmentDirection)) return null;

    const directionFromSharedEdgeToInsideOfThis = this.normalDirection.crossProductDirection(thisSegmentDirection)!;
    const directionFromSharedEdgeToInsideOfOther = other.normalDirection.crossProductDirection(
      thisSegmentDirection.reverse(),
    )!;

    return directionFromSharedEdgeToInsideOfOther.angleTo(directionFromSharedEdgeToInsideOfThis, thisSegmentDirection);
  }

  /**
   * Like `sliceByLine` but assumes `slicer` is contained within `this.asPlane()` for performance reasons. If
   * `normalDirection` is supplied, only polygons on that side of `slicer` are returned.
   */
  private sliceByContainedLine(slicer: Line3D, normalDirection?: Direction3D): Polygon3D[] {
    let slicerIn2D = this.embedding.lineTo2D(slicer);
    if (normalDirection) {
      const normalDirectionIn2D = this.embedding.directionTo2D(normalDirection);
      const lineDirection = normalDirectionIn2D.rotateReverseQuarterTurn();
      slicerIn2D = slicerIn2D.orientWith(lineDirection);
    }
    return this.mappedTo2D
      .slice(slicerIn2D, normalDirection != null)
      .map(this.embedding.polygonTo3D.bind(this.embedding));
  }

  sliceByLine(slicer: Line3D): Polygon3D[] {
    return this.asPlane().containsLine(slicer) ? this.sliceByContainedLine(slicer) : [this];
  }

  slice(slicer: Plane3D): Polygon3D[] {
    const slicingLineFromPlane = this.asPlane().intersectWithPlane(slicer);
    return slicingLineFromPlane ? this.sliceByLine(slicingLineFromPlane) : [this];
  }

  sliceOriented(slicingPlane: Plane3D): Polygon3D[] {
    const slicingLineFromPlane = this.asPlane().intersectWithPlane(slicingPlane);
    if (slicingLineFromPlane) {
      const inPlaneNormalDirection = slicingPlane.halfSpace.direction.reject(this.normalDirection)!;
      return this.sliceByContainedLine(slicingLineFromPlane, inPlaneNormalDirection);
    }

    return slicingPlane.pointIsOnNormalSide(this.centroid) ? [this] : [];
  }

  sliceOrientedByMany(slicingPlanes: readonly Plane3D[]): Polygon3D[] {
    return slicingPlanes.reduce<Polygon3D[]>(
      (slices, slicingPlane) => slices.flatMap((slice) => slice.sliceOriented(slicingPlane)),
      [this],
    );
  }

  extrude(extrusionVector: Vector3D) {
    if (this.normalDirection.isPerpendicularTo(extrusionVector.direction!))
      throw new Error('Extrusion vector cannot be parallel to extruding face.');

    if (distanceEquals(extrusionVector.direction!.dotProduct(this.normalDirection), 0))
      throw new Error('Extrusion vector is too small in the normal direction.');

    const baseFace = this.reverseIf(extrusionVector.direction!.dotProduct(this.normalDirection) > 0);

    const faces = [baseFace];
    const oppositeFaceVertices = [];

    for (const segment of baseFace.edges) {
      const opposite = segment.translate(extrusionVector);

      const sideFace = new Polygon3D([opposite.basePoint, opposite.endPoint, segment.endPoint, segment.basePoint]);

      faces.push(sideFace);
      oppositeFaceVertices.push(opposite.endPoint);
    }
    faces.push(new Polygon3D(oppositeFaceVertices.toReversed()));

    return Polyhedron.fromFaces(faces);
  }

  /**
   * Assumes lineSegment and this are coplanar.
   * @returns a list of the points where lineSegment intersects the edges of the polygon
   */
  intersectionCoplanarPoints(lineSegment: LineSegment3D) {
    if (!this.asPlane().containsLine(lineSegment.toLine())) throw new Error('Segment and polygon are not coplanar');

    const in2D = new LineSegment2D(
      this.embedding.pointTo2D(lineSegment.basePoint),
      this.embedding.pointTo2D(lineSegment.endPoint),
    );

    return sortBy(
      uniqWith(
        this.mappedTo2D.edges.map((edge) => edge.intersectWithSegment(in2D)).filter(notNullish),
        (point1, point2) => point1.equals(point2),
      ),
      (point) => point.distanceTo(lineSegment.basePoint),
    ).map((point) => this.embedding.pointTo3D(point));
  }

  contains(point: Point3D) {
    return this.asPlane().containsPoint(point) && this.mappedTo2D.containsPoint(this.embedding.pointTo2D(point));
  }

  containsOnInside(point: Point3D) {
    return (
      this.asPlane().containsPoint(point) && this.mappedTo2D.containsPointOnInside(this.embedding.pointTo2D(point))
    );
  }

  /**
   * Determines if this polygon contains the entirety of the other polygon.
   */
  containsPolygon(polygon: Polygon3D) {
    return (
      polygon.vertices.every(this.contains.bind(this)) && !this.vertices.some(polygon.containsOnInside.bind(polygon))
    );
  }

  isOrientedWith = ({ normalDirection }: { normalDirection: Direction3D }): boolean =>
    angleLessThan(normalDirection.angleBetween(this.normalDirection), 90);

  orientWith = ({ normalDirection }: { normalDirection: Direction3D }): Polygon3D =>
    angleLessThanOrEqual(normalDirection.angleBetween(this.normalDirection), 90) ? this : this.reverse();

  removeCollinearPoints() {
    const newVertices: Point3D[] = [];

    for (let i = 0; i < this.vertices.length; i++) {
      const basePoint = this.vertices.at(i - 1)!;
      const pointToCheck = this.vertices.at(i)!;
      const endPoint = this.vertices.at(i + 1 - this.vertices.length)!;

      const segment1 = new LineSegment3D(basePoint, pointToCheck);
      const segment2 = new LineSegment3D(pointToCheck, endPoint);
      if (!segment1.direction.isParallelTo(segment2.direction)) {
        newVertices.push(pointToCheck);
      }
    }

    return new Polygon3D(newVertices);
  }

  projectTo2D() {
    const verticesProjectedTo2D = this.vertices.map((vertex) => vertex.projectTo2D());
    return Polygon2D.fromVerticesIfValid(verticesProjectedTo2D);
  }

  translate(vector: Vector3D) {
    return new Polygon3D(this.vertices.map((vertex) => vertex.translate(vector)));
  }
}

export function centerPoint3D(points: readonly Point3D[]) {
  return points.reduce((sum, current) => sum.add(current)).divideScalar(points.length);
}

export function tryGetNormalDirection(points: readonly Point3D[]) {
  // https://www.khronos.org/opengl/wiki/Calculating_a_Surface_Normal#Newell's_Method
  const normalX = sum(circularPairwise(points).map(([v1, v2]) => (v1.y - v2.y) * (v1.z + v2.z)));
  const normalY = sum(circularPairwise(points).map(([v1, v2]) => (v1.z - v2.z) * (v1.x + v2.x)));
  const normalZ = sum(circularPairwise(points).map(([v1, v2]) => (v1.x - v2.x) * (v1.y + v2.y)));

  return Direction3D.fromObject({ x: normalX, y: normalY, z: normalZ });
}

export function getNormalDirection(points: readonly Point3D[]) {
  const result = tryGetNormalDirection(points);
  if (result) return result;
  throw new Error('Could not find normal line.');
}

export function getEmbedding(points: readonly Point3D[], normalDirection: Direction3D) {
  const centerPoint = centerPoint3D(points);
  const nonParallelStandardAxis = minBy([Direction3D.right, Direction3D.up, Direction3D.out], (axis) =>
    Math.abs(axis.dotProduct(normalDirection)),
  )!;
  const planarAxis1 = normalDirection.crossProductDirection(nonParallelStandardAxis)!;
  const planarAxis2 = normalDirection.crossProductDirection(planarAxis1)!;
  const origin = normalDirection.toPoint(vectorFromOrigin(centerPoint).getComponentInDirection(normalDirection));
  return new IsometricEmbedding(planarAxis1, planarAxis2, origin);
}

function vectorFromOrigin({ x, y, z }: Point3D) {
  return new Vector3D(x, y, z);
}

export default Polygon3D;
